import { PhoenixUser } from 'phoenix/domain';
import {
	DeploymentNameService,
	TrackingService
} from 'sharebase/services'
import UsersService from 'sharebase/services/usersService';
import { FullTextSearchDataStore } from 'sharebase/dataStores/fullTextSearchDataStore';
import { AuthService } from "phoenix/services";
import { StaticToggleService } from 'util/services/staticToggleService';
import { 
	IDictionary,
	IStorageAccessor
} from 'util/domain';

require('./searchBox.styles');

const template = require('./searchBox.template');

interface IFileType{
    label: string;
    extention: string;
}

interface SuggestableFormat extends IFileType {
	key:string;
}

interface SearchHistoryItem {
	historyTerm:string;
	formats:SearchFilterItem[];
	tags:SearchFilterItem[];
	owners:SearchFilterItem[];
	searchTime:number;
}

interface SearchFilterItem {
	type:FilterType;
	label:string;
	value:string|number;
}

enum FilterType {
	format = 'format',
	tag = 'tag',
	owner = 'owner'	
}

const RECENT_SEARCH_LOCAL_STORAGE_KEY = 'sharebasecontentSearchHistory';

export function FullTextSearchBoxDirective(
	$interval:ng.IIntervalService
) {
	return {
		restrict: 'E',
		template,
		bindToController: true,
		scope: {
			close: '&onClose',
			searchInFocus: '=',
			searchText: '=phrase',
			searchFilters: '=filters'

		},
		controller: SearchBoxController,
		controllerAs: 'vm',
		link: (scope: ng.IScope, element: ng.IAugmentedJQuery, attrs:ng.IAttributes, vm: SearchBoxController) => {
			element.on('click', () => vm.dataStore.initSearch());
			//wait for template to be compiled

			let searchInput: ng.IAugmentedJQuery;
			let t = $interval(() => {
				if (element.find('input').length > 0) {
					$interval.cancel(t);
					searchInput = element.find('input');
				}
			}, 1000);

			vm.setInputFocus = ()=>{
				if(searchInput){
					vm.$timeout(()=>{				
						//timeout used to keep previous keyboard inputs from being applied to the search input
						searchInput.focus();		
						//used to set the cursor at the end of the input text to allow users to start typing to add more search phrases
						setFocusToEnd(searchInput);								
					},0);
				}
				
			}

			function setFocusToEnd(searchInput){
				let input:HTMLInputElement = <HTMLInputElement>searchInput[0];
				input.selectionStart = input.selectionEnd = 999999;
			}

			scope.$watch(()=>{
				return (vm.searchText || vm.searchFilters.length > 0 || vm.searchInFocus);
			}, (newVal)=>{

				let prevSearchInProgress = vm.searchInProgress;
				vm.searchInProgress = !!newVal;
				if(vm.searchInProgress){
					if(!prevSearchInProgress){
						vm.trackingService.trackClick('Full text search opened');
					}
					
					element.addClass('sb-ft-search---search-in-progress');
				}
				else{					
					element.removeClass('sb-ft-search---search-in-progress');
				}
			});

			vm.navMoveDown = function(){
				let listItems = element.find('md-list-item');
				if(vm.activeListIndex === -1 || vm.activeListIndex > listItems.length-2){ 
					vm.activeListIndex = 0;
				}
				else{
					vm.activeListIndex++;
				}
				angular.element(listItems[vm.activeListIndex]).find('button').focus();
			}

			vm.navMoveUp = function(){
				let listItems = element.find('md-list-item');
				if(vm.activeListIndex === -1 || vm.activeListIndex === 0){ 
					vm.activeListIndex = listItems.length - 1;
				}
				else{
					vm.activeListIndex--;
				}
				angular.element(listItems[vm.activeListIndex]).find('button').focus();
			};

			vm.handleNavInput = (e: KeyboardEvent)=>{
				let key = e.keyCode || e.charCode;
				if (key === 27) { //escape
					if(vm.searchInProgress){
						vm.closeSearch();
					}
					else{
						vm.clearSearch();
					}
				}
				else if (key === 40) { //down arrow
					vm.navMoveDown();
				}
				else if (key === 38) { //up arrow
					vm.navMoveUp();
				}
			}
						
			vm.handleInput = (e: KeyboardEvent)=> {
				let key = e.keyCode || e.charCode;
				if (key === 13 && vm.canSearch) {
					vm.search();
				}	
				else{
					vm.searchInFocus = true;
					if(vm.searchInProgressPhrase && vm.searchText.indexOf(vm.searchInProgressPhrase) === -1){
						vm.searchInProgressPhrase = null;
					}
					vm.matchUsers(vm.searchText);
				}
			};

			vm.handlePreInput= (e: KeyboardEvent)=> {
				let input = <HTMLInputElement>e.target;
				let key = e.keyCode || e.charCode;
				if(key === 8 && input.value === '' && vm.searchFilters.length > 0){ //8 - backspace
					vm.searchFilters.pop();
				}
			};
		}
	};
}
FullTextSearchBoxDirective.$inject = [
	'$interval'
];

export class SearchBoxController {
	private readonly fileTypes:IDictionary<IFileType> = {
		pdf: {
			label: this.gettextCatalog.getString('PDF'), 
			extention: '.pdf'
		},
		excel: {
			label: this.gettextCatalog.getString('Excel'), 
			extention: '.xls'
		},
		word: {
			label: this.gettextCatalog.getString('Word'), 
			extention: '.doc'
		},
		powerpoint: {
			label: this.gettextCatalog.getString('PowerPoint'), 
			extention: '.ppt'
		},
		audio: {
			label: this.gettextCatalog.getString('Audio'), 
			extention: '.wav'
		},
		email: {
			label: this.gettextCatalog.getString('Email'),
			extention: '.msg'
		},
		html: {
			label: this.gettextCatalog.getString('HTML'), 
			extention: '.htm'
		},
		image: { 
			label:this.gettextCatalog.getString('Image'), 
			extention:'.png'
		},
		text: {
			label: this.gettextCatalog.getString('Text'), 
			extention: '.txt'
		},
		video: {
			label: this.gettextCatalog.getString('Video'), 
			extention:'.avi'
		}
	};

	public searchText:string;
	public searchInFocus:boolean = false;
	public searchInProgress:boolean = false;
	public selectedFormats:SearchFilterItem[] = [];
	public selectedTags:SearchFilterItem[] = [];
	public selectedOwners:SearchFilterItem[];
	public users:PhoenixUser[] = [];
	public searchHistory:SearchHistoryItem[] = [];
	private recentId:number;
	public close:Function;
	public handleNavInput:Function;
	public handleInput:Function;
	public handlePreInput:Function;
	public navMoveDown:Function;
	public navMoveUp:Function;
	public activeListIndex:number = -1;
	public searchFilters:SearchFilterItem[] = [];
	public historyLength:number = 3;
	public tagLength:number = 3;
	public typeLength:number = 3;
	public setInputFocus:Function;
	public searchInProgressPhrase:string;

	public suggestFormats:SuggestableFormat[] = [];

	public static readonly $inject = [
		'$state',
		'$timeout',
		'$mdMedia',
		'pxAuthService',
		'sbDeploymentNameService',
		'sbFullTextDataStore',
		'sbTrackingService',
		'sbUsersService',
		'staticToggleService',
		'localStorageService',
		'gettextCatalog',
	];

	constructor(
		private readonly $state:ng.ui.IStateService,
		public readonly $timeout:ng.ITimeoutService,
		public readonly $mdMedia:ng.material.IMedia,
		private readonly authService:AuthService,
		private readonly deploymentNameService:DeploymentNameService,
		public readonly dataStore:FullTextSearchDataStore,
		public readonly trackingService:TrackingService,
		private readonly usersService:UsersService,
		private readonly staticToggleService:StaticToggleService,
		private readonly localStorageService: IStorageAccessor,
		private readonly gettextCatalog:ng.gettext.gettextCatalog
	) {
		this.suggestFormats = Object.keys(this.fileTypes)
			.map(k => {
				let item:any = this.fileTypes[k];
				item.key = k;
				return item as SuggestableFormat;
			});

		this.getRecents();
	}

	get canSearch(){
		return (this.searchText && this.searchText!=='') || this.searchFilters.length > 0;
	}
	
	public setSearchInFocus():void{
		this.searchInFocus = true;
	}

	private setRecentSearch(recent:SearchHistoryItem):void{
		this.searchFilters = [];
		this.selectedTags = recent.tags && recent.tags.length > 0 ? recent.tags : [];
		this.selectedFormats = recent.formats && recent.formats.length > 0 ? recent.formats : [];
		this.selectedOwners = recent.owners && recent.owners.length > 0 ? recent.owners : [];

		//add tags, formats, and owners to UI
		this.selectedTags.forEach(t=>{
			this.searchFilters.push({
				type: FilterType.tag,
				label: t.label,
				value: t.value
			});
		});
		this.selectedFormats.forEach(f=>{
			this.searchFilters.push({
				type: FilterType.format,
				label: f.label,
				value: f.value
			});
		});

		this.selectedOwners.forEach(o=> {
			this.searchFilters.push({
				type: FilterType.owner,
				label: o.label,
				value: o.value
			});
		});

		this.searchText = recent.historyTerm;

		this.setInputFocus();
	}
	
	public recentSearch(recent:SearchHistoryItem):void{
		this.setRecentSearch(recent);
		this.recentId = recent.searchTime;
		this.search();
	}
	
	public getRecentString(item:SearchHistoryItem):string{
		let messages:string[] = [];

		if(item.historyTerm){
			messages.push( `"${item.historyTerm}"`);
		}
		if(item.formats && item.formats.length > 0){
			messages.push(
				this.gettextCatalog.getString('File type {{fileFormats}}', {
					fileFormats: item.formats.map(f => f.label).join(', ')
				})
			)
		}
		if(item.tags && item.tags.length > 0){
			messages.push(
				this.gettextCatalog.getString('Tagged with {{tags}}', {
					tags: item.tags.map(t => t.label).join(', ')
				})
			)
		}
		if(item.owners && item.owners.length > 0){
			messages.push(
				this.gettextCatalog.getString('Owned by {{owners}}', {
					owners: item.owners.map(o => o.label).join(', ')
				})
			)
		}

		return messages.join(', ');
	}

	public addFormat(item:SuggestableFormat){
		let count = this.searchFilters.filter(f=>f.type === FilterType.format && f.value===item.key);
		if(count.length===0){	
			this.searchFilters.push({
				type: FilterType.format,
				label: item.label,
				value: item.key
			});
			this.searchText = this.searchInProgressPhrase ? this.searchInProgressPhrase.trim() : '';
			this.setInputFocus();
		}
	}	
	
	public addTag(item:string){
		let count = this.searchFilters.filter(f=>f.type === FilterType.tag && f.value===item);
		if(count.length===0){
			this.searchFilters.push({
				type: FilterType.tag,
				label: item,
				value: item
			});			
			this.searchText = this.searchInProgressPhrase ? this.searchInProgressPhrase.trim() : '';
			this.setInputFocus();
		}
	}

	public addFileOwner(user:PhoenixUser){
		let count = this.searchFilters.filter(f=>f.type === FilterType.owner && f.value===user.UserId);
		if (count.length===0){
			this.searchFilters.push({
				type: FilterType.owner,
				label: user.UserName,
				value: user.UserId
			});
			this.searchText = this.searchInProgressPhrase ? this.searchInProgressPhrase.trim() : '';
			this.setInputFocus();
		}
	}
	
	public get searchFilterPhrase(){
		return this.searchText ? this.searchText.replace(this.searchInProgressPhrase,'').trim() : '';
	}

	public search(): void {
		this.trackingService.trackClick('Full text search started');
		
		this.dataStore.searchParamsBuilder.setQueryText(this.searchText);
		this.searchInProgressPhrase = this.searchText;

		this.selectedFormats = this.searchFilters.filter(f=>f.type === FilterType.format);
		this.selectedFormats.forEach(f=>{
			this.dataStore.searchParamsBuilder.addFileType(f.value as string);
		});
				
		this.selectedTags = this.searchFilters.filter(f=>f.type === FilterType.tag);
		this.selectedTags.forEach(t=>{
			this.dataStore.searchParamsBuilder.addTag(t.value as string);
		});

		this.selectedOwners = this.searchFilters.filter(f=>f.type === FilterType.owner);
		this.selectedOwners.forEach(c=>{
			this.dataStore.searchParamsBuilder.addUser(c.value as number);
		})

		this.dataStore.load();

		this.addRecentSearch(this.searchText, this.selectedFormats, this.selectedTags, this.selectedOwners);

		//update recent searches
		this.searchHistory = [];
		this.getRecents();

		this.$state.go('content.fulltextsearch');
		
		this.closeSearch();
		this.users = [];
	}

	public clearSearch(){
		this.$timeout(()=>{
			//fix to close the search box on click of recents
			this.resetSearch();
			this.searchInProgressPhrase = null;
		}, 100);

		this.closeSearch();		
		
	}

	public closeSearch(){
		this.$timeout(()=>{
			this.searchInFocus = false;	
		}, 50);

		if(this.close){
			this.$timeout(()=>{
				this.close();
			}, 550);
		}		
	}

	private resetSearch(){
		this.searchText = '';
		this.selectedFormats = [];
		this.selectedTags = [];
		this.users = [];

		//clear filters
		this.searchFilters = [];

		//reset visible count
		this.historyLength = 3;
		this.tagLength = 3;
		this.typeLength = 3;		
	}

	public getRecents(){
		let recentSearches = this.localStorageService.getStoredValue(RECENT_SEARCH_LOCAL_STORAGE_KEY);
		if(recentSearches ===''){			
			this.searchHistory = [];
			return;
		}

		this.searchHistory= JSON.parse(recentSearches);
	}

	public removeRecent(item:SearchHistoryItem){
		let currentRecents:SearchHistoryItem[] = this.localStorageService.getStoredValue(RECENT_SEARCH_LOCAL_STORAGE_KEY) !=='' ? JSON.parse(this.localStorageService.getStoredValue(RECENT_SEARCH_LOCAL_STORAGE_KEY)) : [];
		let newRecents = currentRecents.filter(r=> r.searchTime !== item.searchTime);

		this.localStorageService.setStoredValue(RECENT_SEARCH_LOCAL_STORAGE_KEY,JSON.stringify(newRecents.slice(0,15)));
		this.getRecents();
	}

	public matchUsers(phrase:string) {
		if (!phrase || phrase.length < 2 || !this.staticToggleService.isEnabled('search-box')) {
			this.users = [];
			return;
		}
		phrase = phrase.toLowerCase();
		return this.deploymentNameService.resolveSystemName(this.authService.deploymentId)
			.then(systemName => this.usersService.getUsersTypeAhead(phrase, systemName))
			.then(results => {
				return results.sort((a, b) => {
					let aName = a.UserName.toLowerCase();
					let bName = b.UserName.toLowerCase();

					if(aName === bName) {
						return 0;
					}
					else {
						return aName > bName ? 1 : -1;
					}
				});
			})
			.then(results => this.users = results);
	}

	private addRecentSearch(searchText:string, formats:SearchFilterItem[], tags:SearchFilterItem[], owners:SearchFilterItem[]){
		let currentRecents:SearchHistoryItem[] = this.localStorageService.getStoredValue(RECENT_SEARCH_LOCAL_STORAGE_KEY) !== '' ?
			JSON.parse(this.localStorageService.getStoredValue(RECENT_SEARCH_LOCAL_STORAGE_KEY)) : [];
			
		if(this.recentId) {
			//update the clicked recent with current time
			let recent = currentRecents.filter(r=> r.searchTime === this.recentId);
			if(recent.length===1){
				let recentIndex = currentRecents.indexOf(recent[0]);
				//update recent search time
				recent[0].searchTime = Date.now();
				//remove current index
				currentRecents.splice(recentIndex,1);
				//add updated recent
				currentRecents.unshift(recent[0]);
			}
		}
		else{
			//create new search object
			let entry = {
				historyTerm: searchText,
				formats: formats || [],
				tags: tags || [],
				owners: owners || [],
				excludeMine: false,
				onlyMine:false,
				searchTime: Date.now()
			};

			//add to top of recent search arr
			//limit to last 15 searches
			currentRecents.unshift(entry);

		}

		this.localStorageService.setStoredValue(RECENT_SEARCH_LOCAL_STORAGE_KEY,JSON.stringify(currentRecents.slice(0,15)));
		this.recentId = null;
		this.getRecents();
	}

}


// WEBPACK FOOTER //
// ./src/sharebase/components/fullText/searchBox/searchBox.ts